/**
 * Custom Hook for Clearing Form State
 *
 * Provides a reusable function to clear generated content state
 * when form fields change. Used in AdminPanel for form validation.
 *
 * @module useFormStateClear
 */

import { Dispatch, SetStateAction } from 'react';

/**
 * Hook that provides a function to clear all generated content states.
 * This is useful when form fields change and we want to reset
 * the generated questions, transcripts, and sample questions.
 *
 * @param setGeneratedQuestions - Setter for generated questions state
 * @param setGeneratedTranscripts - Setter for generated transcripts state
 * @param setSampleQuestions - Setter for sample questions state
 * @returns Function to clear all generated content
 *
 * @example
 * const clearContent = useFormStateClear(
 *   setGeneratedQuestions,
 *   setGeneratedTranscripts,
 *   setSampleQuestions
 * );
 *
 * // In onChange handler:
 * onValueChange={(value) => {
 *   setGenerationForm(prev => ({ ...prev, examType: value }));
 *   clearContent();
 * }}
 */
export function useFormStateClear(
  setGeneratedQuestions: Dispatch<SetStateAction<any[]>>,
  setGeneratedTranscripts: Dispatch<SetStateAction<any[]>>,
  setSampleQuestions: Dispatch<SetStateAction<any[]>>,
): () => void {
  const clearGeneratedContent = () => {
    setGeneratedQuestions([]);
    setGeneratedTranscripts([]);
    setSampleQuestions([]);
  };

  return clearGeneratedContent;
}
